import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { AddressRegion } from '../../_kernel/AddressRegion'
import { DateModel } from '../../_kernel/DateModel'
import { PostalCode } from '../../_kernel/PostalCode'
import { Title } from '../../_kernel/Title'
import { Language } from '../../shared/domain/Language'
import { Property } from './Property'
import { PropertyDescription } from './PropertyDescription'
import { License } from './License'
import type { GetV3PropertyResponse } from '../infrastructure/HTTPPropertyRepository/schemas/v3/property/:country/:city/:slug'
import type { Description } from '../../_kernel/Description'

export const PropertyDetailsValidation = z.object({
  property: z.instanceof(Property, { message: '[PropertyDetails] property is required' }),
  checkinTime: z
    .instanceof(DateModel, { message: '[PropertyDetails] checkinTime is required' })
    .optional(),
  checkoutTime: z
    .instanceof(DateModel, { message: '[PropertyDetails] checkoutTime is required' })
    .optional(),
  postalCode: z.instanceof(PostalCode, { message: '[PropertyDetails] postalCode is required' }),
  addressRegion: z.instanceof(AddressRegion, {
    message: '[PropertyDetails] addressRegion is required',
  }),
  language: z.instanceof(Language, { message: '[PropertyDetails] language is required' }),
  title: z.instanceof(Title, { message: '[PropertyDetails] title is required' }).optional(),
  description: z.instanceof(PropertyDescription, {
    message: '[PropertyDetails] description is required',
  }),
  license: z.instanceof(License, { message: '[PropertyDetails] license is required' }).optional(),
})

export class PropertyDetails extends Model {
  static create({
    property,
    postalCode,
    addressRegion,
    checkinTime,
    checkoutTime,
    language,
    description,
    license,
    title,
  }: {
    property: Property
    postalCode: PostalCode
    addressRegion: AddressRegion
    checkinTime?: DateModel
    checkoutTime?: DateModel
    language: Language
    description: PropertyDescription
    license?: License
    title?: Title
  }) {
    PropertyDetailsValidation.parse({
      property,
      checkinTime,
      checkoutTime,
      postalCode,
      addressRegion,
      language,
      description,
      license,
    })
    return new PropertyDetails(
      property,
      postalCode,
      addressRegion,
      language,
      description,
      title,
      checkinTime,
      checkoutTime,
      license
    )
  }

  static fromPropertyV3API({ json }: { json: GetV3PropertyResponse }) {
    const language = Language.create({ value: json.language })
    const descriptionContent = json.description?.[json.language]

    return PropertyDetails.create({
      property: Property.fromPropertyV3API({ json }),
      postalCode: PostalCode.create({ value: json.postal_code }),
      addressRegion: AddressRegion.create({ value: json.location?.subdivision_1_name ?? '' }),
      checkinTime:
        DateModel.fromDateTimeOrNull(json.conditions?.checkin?.begin_time ?? '') ?? undefined,
      checkoutTime:
        DateModel.fromDateTimeOrNull(json.conditions?.checkout?.time ?? '') ?? undefined,
      language,
      description: PropertyDescription.fromJSON({
        json: {
          amenities: descriptionContent?.amenities,
          businessAmenities: descriptionContent?.business_amenities,
          attractions: descriptionContent?.attractions,
          location: descriptionContent?.location,
          headline: descriptionContent?.headline,
          dining: descriptionContent?.dining,
          rooms: descriptionContent?.rooms,
        },
      }),
      ...(json.license &&
        json.license_name && {
          license: License.create({ name: json.license_name, value: json.license }),
        }),
    })
  }

  static fromJSON({ json }: { json: ReturnType<PropertyDetails['toJSON']> }) {
    return PropertyDetails.create({
      property: Property.fromJSON({ json: json.property }),
      postalCode: PostalCode.create({ value: json.postalCode }),
      addressRegion: AddressRegion.create({ value: json.addressRegion }),
      ...(json.checkinTime && { checkinTime: DateModel.create({ value: json.checkinTime }) }),
      ...(json.checkoutTime && { checkoutTime: DateModel.create({ value: json.checkoutTime }) }),
      language: Language.fromJSON(json.language),
      title: json.title ? Title.create({ value: json.title.value }) : undefined,
      description: PropertyDescription.fromJSON({ json: json.description }),
      ...(json.license && { license: License.create(json.license) }),
    })
  }

  static fromJSONOrNull({ json }: { json: ReturnType<PropertyDetails['toJSON']> }) {
    try {
      return PropertyDetails.fromJSON({ json })
    } catch {
      return null
    }
  }

  constructor(
    private readonly _property: Property,
    private readonly _postalCode: PostalCode,
    private readonly _addressRegion: AddressRegion,
    private readonly _language: Language,
    private readonly _description: PropertyDescription,
    private readonly _title?: Title,
    private readonly _checkinTime?: DateModel,
    private readonly _checkoutTime?: DateModel,
    private readonly _license?: License
  ) {
    super()
  }

  id() {
    return this._property.id
  }

  propertyName() {
    return this._property.name
  }

  enhanceMetadata(title: Title, description: Description) {
    return PropertyDetails.fromJSON({
      json: {
        ...this.toJSON(),
        title: title.toJSON(),
        description: PropertyDescription.create({ headline: description }).toJSON(),
      },
    })
  }

  propertyTitle() {
    return this._title?.value ?? `${this.propertyName()} - Stayforlong`
  }

  propertyDescription() {
    return this._description.description()
  }

  propertyImages() {
    return this._property.images
  }

  propertyMainImage() {
    return this._property.mainImage()
  }

  hasLicense() {
    return !!this._license
  }

  license() {
    return this._license
  }

  toJSON() {
    return {
      property: this._property.toJSON(),
      checkinTime: this._checkinTime?.value ?? null,
      checkoutTime: this._checkoutTime?.value ?? null,
      postalCode: this._postalCode.getPostalCode(),
      addressRegion: this._addressRegion.getAddressRegion(),
      language: this._language.toJSON(),
      description: this._description.toJSON(),
      title: this._title?.toJSON() ?? null,
      license: this._license?.toJSON() ?? null,
    }
  }

  getRichHeadJSON(canonicalURL: string) {
    const ldJSON: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Hotel',
    }

    if (this._property.name) ldJSON.name = this._property.name
    if (canonicalURL) ldJSON.url = canonicalURL

    const { latitude, longitude } = this._property.geoCoordinate
    if (latitude && longitude) {
      ldJSON.geo = {
        '@type': 'GeoCoordinates',
        latitude,
        longitude,
      }
    }

    if (this._property.stars) {
      ldJSON.starRating = {
        '@type': 'https://schema.org/Rating',
        ratingValue: this._property.stars,
      }
    }

    if (this._property.rating && this._property.numberOfComments) {
      ldJSON.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: this._property.rating,
        reviewCount: this._property.numberOfComments,
        bestRating: 10,
      }
    }

    ldJSON.logo = 'https://static.stayforlong.com/web/images/sfl-logo_x2.png'

    const firstImage = this._property.mainImage()
    if (firstImage) {
      ldJSON.image = `https://${firstImage.bucket}/${firstImage.path}`
    }

    if (this._checkinTime) ldJSON.checkinTime = this._checkinTime.value
    if (this._checkoutTime) ldJSON.checkoutTime = this._checkoutTime.value

    if (this._property._price) {
      ldJSON.priceRange = `${this._property._price.total}€`
    }

    if (
      this._property.address ||
      this._property.city ||
      this._property.country ||
      this._addressRegion.getAddressRegion() ||
      this._postalCode.getPostalCode()
    ) {
      const address: Record<string, string> = {
        '@type': 'PostalAddress',
      }
      if (this._property.address) address.streetAddress = this._property.address
      if (this._property.city) address.addressLocality = this._property.city
      if (this._addressRegion.getAddressRegion())
        address.addressRegion = this._addressRegion.getAddressRegion()
      if (this._postalCode.getPostalCode()) address.postalCode = this._postalCode.getPostalCode()
      if (this._property.country) address.addressCountry = this._property.country
      ldJSON.address = address
    }

    if (this._property._facilities && this._property._facilities.list.length > 0) {
      ldJSON.amenityFeature = this._property._facilities.list.map(facility => ({
        '@type': 'LocationFeatureSpecification',
        name: facility.id,
        value: 'True',
      }))
    }

    return ldJSON
  }
}
